import React, { useState, useEffect, useMemo, useCallback } from 'react'
import moment from 'moment'
import { toJS } from 'mobx';
import { HORIZONTAL_ORIENTATION, ANCHOR_RIGHT } from 'react-dates/constants';
import { SingleDatePicker } from 'react-dates';
import PageLoader from 'components/PageLoader';
import Button from 'components/Button';
import Api from 'utils/Api';
import 'react-dates/lib/css/_datepicker.css';
import './TableView.scss';

const TableView = (props) => {

    const [focused, setFocused] = useState(false)
    const [loadingContent, setLoadingContent] = useState(false)
    const [carouselHoursHeight, setCarouselHoursHeight] = useState(undefined)
    const carouselHoursRefCallBack = useCallback(
        (carouselHoursRef) => {
            if (carouselHoursRef !== null) {
                setCarouselHoursHeight(carouselHoursRef.getBoundingClientRect().height);
            }
        },
        [],
    )

    const clearModalOnOutsideClick = event => {
        const hours = Array.from(document.querySelectorAll('.hour-card'));
        const modals = Array.from(document.querySelectorAll('.modal-selected-date'));

        if (!hours.reduce((contain, e) => e.contains(event.target) || contain, false)
            && !modals.reduce((contain, e) => e.contains(event.target) || contain, false)
            && !event.target.classList.contains('modal-btn-continue')) {
            props.handleSetHour({})
        }
    }

    const loadSchedules = (loadSchedule, adventures, date) => {
        setLoadingContent(true)
        const promises = adventures.map(adventure => loadSchedule(date, adventure.pkAdventure)).filter(p => typeof p.then === 'function')
        Promise.all(promises).then(() => { setLoadingContent(false); })
    }

    const checkIfMonthIsLoaded = (date, schedule) => schedule[moment(date).format("YYYY-MM-DD")] === undefined

    useEffect(() => {
        window.addEventListener('click', clearModalOnOutsideClick);
        return () => {
            window.removeEventListener('click', clearModalOnOutsideClick);
        }
    }, []);

    const hasAdventures = useMemo(() => !!props.adventures.length, [props.adventures])

    useEffect(date => {
        const schedule = toJS(props.schedule)
        const adventures = toJS(props.adventures)

        if (schedule && Object.keys(schedule).length && adventures && adventures.length) {
            if (checkIfMonthIsLoaded(props.selectedDate, schedule[adventures[0].pkAdventure])) {
                loadSchedules(props.loadSchedule, adventures, props.selectedDate)
            }
        }
    }, [props.selectedDate]);

    // Verifica os dias com horário disponível
    const isDayBlocked = useCallback((day) => {
        const formattedDay = day.format("YYYY-MM-DD");

        // Verifica se algum dia do mês atual tem horas disponíveis
        const hasAnyAvailableHoursInMonth = () => {
            const daysInMonth = day.daysInMonth();
            let availableDaysCount = 0;
            let onlyFirstDayHasData = false;

            for (let i = 1; i <= daysInMonth; i++) {
                const checkDay = day.clone().date(i).format("YYYY-MM-DD");
                if (props.adventures.some(adventure => {
                    const scheduleForAdventure = props.schedule[adventure.pkAdventure];
                    if (scheduleForAdventure && scheduleForAdventure[checkDay]) {
                        const { availableHours } = scheduleForAdventure[checkDay];
                        return availableHours.some(hour => !hour.blocked);
                    }
                    return false;
                })) {
                    availableDaysCount++;

                    if (i === 1) {
                        onlyFirstDayHasData = true;
                    } else {
                        onlyFirstDayHasData = false;
                    }
                }
            }

            // Se apenas o primeiro dia tem dados e não há mais dias disponíveis no mês, retornamos false
            if (onlyFirstDayHasData && availableDaysCount === 1) {
                return false;
            }

            return availableDaysCount > 0;
        }

        if (!hasAnyAvailableHoursInMonth()) {
            return false; // Permite que todos os dias do mês sejam selecionáveis
        }

        // Verifica se a data está entre os dias que não têm disponibilidade
        return !props.adventures.some(adventure => {
            const scheduleForAdventure = props.schedule[adventure.pkAdventure];

            // Verifica se há um agendamento para a aventura nesse dia
            if (scheduleForAdventure && scheduleForAdventure[formattedDay]) {
                const { availableHours } = scheduleForAdventure[formattedDay];

                // Verifica se há pelo menos uma hora disponível e não bloqueada nesse dia
                return availableHours.some(hour => !hour.blocked);
            }

            // Se não houver um agendamento para a aventura nesse dia, considera que o dia não está bloqueado
            return false;
        });
    }, [props.adventures, props.schedule]);



    // const dates = props.visibleDates.map(curDate => (
    //     <div className={`date-card ${moment(props.selectedDate).isSame(curDate, 'day') ? 'selected' : ''}`}
    //         onClick={() => props.handleSetDate(curDate.toDate())}
    //     >
    //         <span>{moment().isSame(curDate, 'day') ? 'Hoje' : curDate.format('ddd')}</span>
    //         <span>{curDate.format('DD/MM')}</span>
    //     </div>
    // ))

    // const settings = {
    //     dots: false,
    //     arrows: true,
    //     autoplay: false,
    //     infinite: false,
    //     speed: 500,
    //     slidesToShow: 4,
    //     slidesToScroll: 3,
    //     pauseOnHover: true,
    //     autoplaySpeed: 3500,
    //     responsive: [
    //         {
    //             breakpoint: 1024,
    //             settings: {
    //                 slidesToShow: 3,
    //                 slidesToScroll: 3,
    //                 infinite: false,
    //                 dots: true
    //             }
    //         },
    //         {
    //             breakpoint: 600,
    //             settings: {
    //                 slidesToShow: 2,
    //                 slidesToScroll: 2,
    //                 infinite: false,
    //             }
    //         },
    //         {
    //             breakpoint: 300,
    //             settings: {
    //                 slidesToShow: 1,
    //                 slidesToScroll: 1,
    //                 infinite: false,
    //             }
    //         }
    //     ]
    // };

    // Processa e extrai os dados relevantes das aventuras
    const processedAdventures = props.adventures.map(adventure => {
        let hours;
        if (props.schedule[adventure.pkAdventure] &&
            props.schedule[adventure.pkAdventure][moment(props.selectedDate).format("YYYY-MM-DD")] &&
            props.schedule[adventure.pkAdventure][moment(props.selectedDate).format("YYYY-MM-DD")].availableHours) {
            hours = props.schedule[adventure.pkAdventure][moment(props.selectedDate).format("YYYY-MM-DD")].availableHours;
        }
        return {
            adventure,
            hours,
            hasHours: !!hours
        };
    });

    // Ordena as aventuras
    const sortedAdventures = processedAdventures.sort((a, b) => Number(!a.hasHours) - Number(!b.hasHours));

    // Cria uma lista única de horas a partir de todas as aventuras
    const allHours = [];
    sortedAdventures.forEach(({ adventure, hours, hasHours }) => {
        if (hasHours) {
            hours.forEach(o => {
                allHours.push({
                    ...o,
                    adventure
                });
            });
        }
    });

    // Ordena allHours por o.start
    allHours.sort((a, b) => moment(a.start, "HH:mm").isBefore(moment(b.start, "HH:mm")) ? -1 : 1);


    return (
        <div className='GridView'>
            <PageLoader
                loading={props.firstLoadingGetSchedule || props.firstLoading}
            >
                {hasAdventures ?
                    <React.Fragment>
                        {/* CAROUSEL DE DATAS */}
                        {/* <h3>Escolha a Data:</h3> */}
                        <SingleDatePicker
                            date={props.selectedDate ? moment(props.selectedDate) : moment()}
                            placeholder={"SELECIONE"}
                            focused={true}
                            onDateChange={date => {
                                props.handleSetDate(date.toDate())
                                props.buildDates(date.toDate())
                            }}
                            anchorDirection={ANCHOR_RIGHT}
                            initialVisibleMonth={() => props.selectedDate ? moment(props.selectedDate) : moment()}
                            onFocusChange={({ focused }) => setFocused(focused)}
                            isOutsideRange={() => false}
                            displayFormat="DD/MM/YYYY"
                            numberOfMonths={1}
                            orientation={HORIZONTAL_ORIENTATION}
                            isDayBlocked={isDayBlocked}
                        />
                        {/* Tabela DE AVENTURAS */}
                        <PageLoader loading={loadingContent}>
                            <div className="carousel-hours" ref={carouselHoursRefCallBack}>
                                <div className={`hour-column`}>
                                    {allHours.length > 0 ? allHours.map(o => {
                                        const adventure = o.adventure;

                                        const requests = props.getRequestsInHour(o, moment(props.selectedDate).format("YYYY-MM-DD"), adventure);
                                        const vacancies = props.getVancancies(adventure, o);
                                        const people = props.getPeople(adventure, o);
                                        const selected = props.selectedHour.value === o.start && props.selectedAdventure && props.selectedAdventure.pkAdventure === adventure.pkAdventure ? 'selected' : '';
                                        const firstRequest = requests.length > 0 ? requests[0] : undefined;
                                        const publicEvent = adventure.only_public || (firstRequest && firstRequest.type == 1);

                                        let disabled = '';
                                        if (publicEvent) {
                                            if (vacancies <= 0) {
                                                disabled = 'disabled';
                                            } else if (firstRequest && (moment(firstRequest.date).diff(moment(), 'hours') <= adventure.block_participation_hours_before)) {
                                                disabled = 'disabled';
                                            }
                                        }

                                        if (firstRequest && firstRequest.type != 1) {
                                            disabled = 'disabled';
                                        }

                                        if (!firstRequest && (o.blocked || o.GC_marked)) {
                                            disabled = 'disabled';
                                        }

                                        return (
                                            <div className='hour-card'>
                                                <div className={`hour-content ${disabled ? disabled : selected}`} onClick={() => {
                                                    if (!disabled) {
                                                        props.selectAdventure(adventure.pkAdventure);
                                                        props.handleSetHour({
                                                            name: o.start,
                                                            value: o.start,
                                                            start: o.start,
                                                            pk_schedule_rule: o.pk_schedule_rule
                                                        });
                                                    }
                                                }}>
                                                    <ModalSelectedDate
                                                        display={disabled ? disabled : selected}
                                                        hour={o.start}
                                                        date={props.selectedDate}
                                                        adventure={adventure.title}
                                                        only_public={adventure.only_public || (requests.length > 0 && requests[0].type == 1)}
                                                        vacancies={vacancies}
                                                        onContinue={props.onContinue}
                                                        pkAdventure={adventure.pkAdventure}
                                                        pkScheduleRule={o.pk_schedule_rule} />
                                                </div>
                                            </div>
                                        );
                                    }) : null}

                                    {sortedAdventures.map(({ adventure, hasHours }) => {
                                        if (!hasHours) {
                                            return (
                                                <div className='hour-card-nodata'>Não há horários para "{adventure.title}"</div>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            </div>
                        </PageLoader>
                    </React.Fragment>
                    :
                    <h4>Infelizmente não há datas disponíveis para fazer a reserva online. Entre em contato conosco e saiba mais.</h4>
                }
            </PageLoader>
        </div>
    )
}

const ModalSelectedDate = (props) => {
    const [price, setPrice] = useState();
    useEffect(() => {
        Api.call('/schedule-rule/avaiable-packages-for-hour', 'get', { pkAdventure: props.pkAdventure, voucherSelected: false, pk_schedule_rule: props.pkScheduleRule })
            .then(response => {
                setPrice(response.data.data[0].priceWithDiscount ? response.data.data[0].priceWithDiscount : response.data.data[0].price)
            })
    }
    )
    return (
        <div className={`modal-selected-date ${props.display}`}>
            <div className="modal-container">
                <div className='modal-content'>
                    <span className="hour"> {props.hour} </span>
                    {/* <span className="date"> {moment(props.date).format('DD/MM')}</span> */}
                </div>
                <span className="modal-title">{props.adventure}</span>
            </div>
            <div className="modal-footer">
                <div className="modal-text">
                    <span className='vacancies'>
                        {props.display === 'disabled' ?
                            'Indisponível'
                            :
                            props.only_public ? `${props.vacancies} vagas` : 'Horário ainda disponível'
                        }
                    </span>
                    <span className='price'>{props.display !== 'disabled' ? 'R$ ' + parseFloat(price).toFixed(0) : ''}
                    </span>
                </div>
                <Button className={`modal-btn-continue ${props.display}`} size="x-sm" type='primary'
                    onClick={props.onContinue}>Reservar</Button>
            </div>
        </div>
    )
}

export default TableView